import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef } from "react";

const GRID_FOCUS_RESTORE_DELAY_MS = 25;

interface UseGridDialogFocusRestoreParams {
  api: GridApi | null | undefined;
  isDialogOpen: boolean;
  displayedRowIndex: number | null;
  displayColumnId: string;
}

const isGridCellSelected = (
  api: GridApi,
  displayedRowIndex: number,
  displayColumnId: string,
): boolean =>
  (api.getCellRanges() ?? []).some(range => {
    const startRowIndex = range.startRow?.rowIndex;
    const endRowIndex = range.endRow?.rowIndex;
    if (startRowIndex === undefined || endRowIndex === undefined) {
      return false;
    }

    const rowStartIndex = Math.min(startRowIndex, endRowIndex);
    const rowEndIndex = Math.max(startRowIndex, endRowIndex);
    return (
      displayedRowIndex >= rowStartIndex &&
      displayedRowIndex <= rowEndIndex &&
      range.columns.some(column => column.getColId() === displayColumnId)
    );
  });

export function useGridDialogFocusRestore({
  api,
  isDialogOpen,
  displayedRowIndex,
  displayColumnId,
}: UseGridDialogFocusRestoreParams) {
  const isDialogOpenRef = useRef(isDialogOpen);
  const restoreTimeoutRef = useRef<number | null>(null);
  const restoreFrameRef = useRef<number | null>(null);
  isDialogOpenRef.current = isDialogOpen;

  const cancelPendingRestore = useCallback(() => {
    if (restoreTimeoutRef.current !== null) {
      window.clearTimeout(restoreTimeoutRef.current);
      restoreTimeoutRef.current = null;
    }
    if (restoreFrameRef.current !== null) {
      cancelAnimationFrame(restoreFrameRef.current);
      restoreFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isDialogOpen) {
      cancelPendingRestore();
    }
  }, [cancelPendingRestore, isDialogOpen]);

  useEffect(() => cancelPendingRestore, [cancelPendingRestore]);

  const handleCloseAutoFocus = useCallback(
    (event: Event) => {
      event.preventDefault();
      cancelPendingRestore();

      restoreTimeoutRef.current = window.setTimeout(() => {
        restoreTimeoutRef.current = null;
        if (isDialogOpenRef.current) {
          return;
        }

        restoreFrameRef.current = requestAnimationFrame(() => {
          restoreFrameRef.current = null;
          if (!api || displayedRowIndex === null || isDialogOpenRef.current) {
            return;
          }

          try {
            api.setFocusedCell(displayedRowIndex, displayColumnId);

            if (!isGridCellSelected(api, displayedRowIndex, displayColumnId)) {
              api.clearCellSelection();
              api.addCellRange({
                rowStartIndex: displayedRowIndex,
                rowEndIndex: displayedRowIndex,
                columns: [displayColumnId],
              });
            }
          } catch {
            // Grid may have been disposed while the dialog was open.
          }
        });
      }, GRID_FOCUS_RESTORE_DELAY_MS);
    },
    [api, cancelPendingRestore, displayedRowIndex, displayColumnId],
  );

  return { handleCloseAutoFocus };
}
